import React, { useState } from 'react';
import styled from 'styled-components';
import { compact } from 'lodash';
import { FinalButton } from '../../Components/FinalButton';
import { InlineSVG } from '../../Components/Icons';
import { MantraSpinner } from '../../Components/LoadingOverlay';
import { mediaScreenFeatures } from '../../Components/Video/common';
import { VideoPageIconButton } from '../../Components/Video/VideoComponents';
import { AddressFragment, useVideoCallSidebarQuery } from '../../graphQL';
import { cx } from '../../utils';
import { VideoCallPageProps } from './VideoCallPage';
import { Tabs } from '../../Components/Tabs';
import { StudentTab } from './SidebarTabs/StudentTab';
import { CrisisTab } from './SidebarTabs/CrisisTab';
import { SafeOperatingHandbookTab } from './SidebarTabs/SafeOperatingHandbookTab';

type VideoCallSidebarProps = {} & Pick<VideoCallPageProps, 'appointment'>;

export const VideoCallSidebar = ({ appointment }: VideoCallSidebarProps) => {
  const { data } = useVideoCallSidebarQuery({ variables: { id: appointment.user.id } });
  const [hidden, setHidden] = useState(window.innerWidth < 744);

  if (!data)
    return (
      <VideoSidebarContainer className={cx({ hidden })}>
        <MantraSpinner simple className="center mt5" />
      </VideoSidebarContainer>
    );

  const options = { vertical: true };

  return (
    <>
      <FinalButton onClick={() => setHidden(false)} kind="white" pill>
        <div className="flex gap-2 items-center">
          <InlineSVG icon="expand" kind="black" width={16} height={16} />
          <SideBarButtonText className="nowrap">Open Sidebar</SideBarButtonText>
        </div>
      </FinalButton>
      {!hidden && (
        <VideoSidebarContainer>
          <div className="flex justify-end nb3">
            <VideoPageIconButton
              onClick={() => setHidden(true)}
              size="tiny"
              toggled
              icon={{ icon: 'collapse', kind: 'black' }}
            />
          </div>
          <div className="h-100 flex flex-column mt2 items-stretch">
            <Tabs
              initialTab="patient"
              config={{
                tabpanel: ({ children }) => (
                  <div className="overflow-y-auto flex-1">{children}</div>
                ),
              }}
              tabs={compact([
                {
                  title: 'Patient',
                  key: 'patient',
                  render: () =>
                    StudentTab({
                      data,
                      appointment,
                    }),
                },
                {
                  title: 'Handbook',
                  key: 'handbook',
                  render: () => SafeOperatingHandbookTab({ data, options }),
                },
                {
                  title: 'Crisis',
                  key: 'crisis',
                  render: () => CrisisTab({ data, appointment }),
                },
              ])}
            />
          </div>
        </VideoSidebarContainer>
      )}
    </>
  );
};

export const formatUserAddress = (address: AddressFragment) => {
  return `${address.line1} ${address.line2} ${address.city}, ${address.state} ${address.zip}`;
};

const SideBarButtonText = styled.span`
  @media only screen and (${mediaScreenFeatures.mobile}) {
    display: none;
  }
`;

const VideoSidebarContainer = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  width: min(26rem, 80%);
  background-color: white;
  padding: 1rem 1.25rem 1rem 1.25rem;
  box-shadow: 0 0 5px 0 gray;
  z-index: 2; // one layer above controls

  transition: transform 0.4s;
  will-change: transform;

  & section {
    margin-bottom: 2rem;
  }

  &.hidden {
    transform: translateX(-28rem);

    & section {
      display: none;
    }
  }
`;
